import { useId, useInsertionEffect } from 'react';

/**
 * Folhas de estilo montadas pelo JavaScript (tema do Guia visual, prévias do visual do cliente).
 *
 * O CSP de produção (`infra/prod/Caddyfile`) tem `style-src 'self'`: o navegador ignora, sem erro,
 * todo `<style>` escrito na página. Folha construída (`new CSSStyleSheet` + `adoptedStyleSheets`)
 * não é estilo embutido e vale com esse CSP. Nunca volte a usar `<style>` nas telas.
 */
const sheets = new Map<string, CSSStyleSheet>();
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((fn) => fn());

/**
 * Avisa quando alguma folha muda. A prévia do estúdio (um quadro à parte) copia as folhas da
 * página: folha construída só vale no documento que a criou.
 */
export function onSheetsChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Grava (ou troca) a folha `key`, por cima do styles.css. */
export function setSheet(key: string, css: string): void {
  let sheet = sheets.get(key);
  if (!sheet) {
    sheet = new CSSStyleSheet();
    sheets.set(key, sheet);
    document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
  }
  sheet.replaceSync(css);
  changed();
}

/** Tira a folha `key`. */
export function removeSheet(key: string): void {
  const sheet = sheets.get(key);
  if (!sheet) return;
  sheets.delete(key);
  document.adoptedStyleSheets = document.adoptedStyleSheets.filter((s) => s !== sheet);
  changed();
}

/** A folha de um componente: vale enquanto ele está na tela e acompanha o `css` a cada mudança. */
export function useSheet(css: string): void {
  const key = `sheet-${useId()}`;
  useInsertionEffect(() => {
    setSheet(key, css);
  }, [key, css]);
  useInsertionEffect(() => () => removeSheet(key), [key]);
}
